import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, Mail, Phone, MapPin } from "lucide-react";

const PROBLEM_TYPES = [
  "Demande d'information",
  "Problème avec une offre",
  "Devenir partenaire",
  "Adhésion entreprise / association",
  "Signaler un abus",
  "Autre",
];

export default function Contact() {
  useSeo({
    title: "Contact — PROXIreduc-28",
    description: "Une question sur PROXIreduc-28 ? Contactez l'équipe de la plateforme locale de l'Eure-et-Loir. Nous répondons aux habitants, professionnels, associations et clubs du département.",
    canonicalPath: "/contact",
    image: "/og-image.jpg",
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "",
    problem_type: "Demande d'information", message: "", gdpr_consent: false });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [site, setSite] = useState({});
  useEffect(() => { api.get("/site-settings").then((r)=>setSite(r.data || {})).catch(()=>{}); }, []);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/requests", { ...form, origin_page: window.location.pathname, source: "site" });
      setSent(true);
      toast.success("Demande envoyée !");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'envoyer la demande.");
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
          <CheckCircle2 size={32}/>
        </div>
        <h1 className="mt-4 font-heading text-3xl font-extrabold">Message reçu !</h1>
        <p className="mt-2 text-slate-600">Nous revenons vers vous rapidement à l'adresse indiquée.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-14">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto rounded-xl bg-orange-50 text-[#E05638] grid place-items-center"><MessageCircle size={22}/></div>
        <h1 className="mt-3 font-heading text-3xl md:text-4xl font-extrabold">Nous contacter</h1>
        <p className="mt-2 text-slate-600">Une question, un problème, une envie de rejoindre la plateforme ? Écrivez-nous.</p>
      </div>

      <form onSubmit={submit} className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="contact-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Nom complet</Label><Input required value={form.name} onChange={set("name")} data-testid="contact-name"/></div>
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="contact-email"/></div>
          <div><Label>Téléphone</Label><Input value={form.phone} onChange={set("phone")} data-testid="contact-phone"/></div>
          <div><Label>Ville</Label><Input value={form.city} onChange={set("city")} data-testid="contact-city"/></div>
        </div>
        <div>
          <Label>Type de demande</Label>
          <Select value={form.problem_type} onValueChange={(v)=>setForm((s)=>({...s, problem_type: v}))}>
            <SelectTrigger data-testid="contact-type"><SelectValue/></SelectTrigger>
            <SelectContent>{PROBLEM_TYPES.map((p)=>(<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div><Label>Message</Label><Textarea rows={5} required value={form.message} onChange={set("message")} data-testid="contact-message"/></div>

        <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50 cursor-pointer hover:border-slate-300" data-testid="contact-gdpr-wrapper">
          <input type="checkbox" required checked={form.gdpr_consent}
                 onChange={(e)=>setForm((s)=>({...s, gdpr_consent: e.target.checked}))}
                 className="mt-1 w-4 h-4" data-testid="contact-gdpr-optin"/>
          <span className="text-xs text-slate-600 leading-relaxed">
            J'accepte que mes informations soient utilisées pour traiter ma demande. Elles sont conservées 3 ans à des
            fins de gestion et sont destinées à l'équipe PROXIreduc-28. Conformément au RGPD, je dispose d'un droit
            d'accès, de rectification et de suppression via <a href="/confidentialite" className="text-[#E05638] underline">notre politique de confidentialité</a> ou par email à{" "}
            <a href={`mailto:${site.contact_email || "info.proxireduc@gmail.com"}`} className="text-[#E05638] underline">{site.contact_email || "info.proxireduc@gmail.com"}</a>.
          </span>
        </label>

        <Button type="submit" disabled={loading || !form.gdpr_consent} className="w-full btn-shine text-white h-11" data-testid="contact-submit">
          {loading ? "Envoi…" : "Envoyer ma demande"}
        </Button>
      </form>

      {(site.contact_email || site.contact_phone || site.contact_address) && (
        <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
          {site.contact_email && (
            <a href={`mailto:${site.contact_email}`} className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-xl hover:border-[#E05638]" data-testid="contact-info-email">
              <Mail size={14} className="text-[#E05638]"/>{site.contact_email}
            </a>
          )}
          {site.contact_phone && (
            <div className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-xl" data-testid="contact-info-phone">
              <Phone size={14} className="text-[#E05638]"/>{site.contact_phone}
            </div>
          )}
          {site.contact_address && (
            <div className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-xl" data-testid="contact-info-address">
              <MapPin size={14} className="text-[#E05638]"/>{site.contact_address}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
